const BLOCK_TAGS = ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'];

const HEADING_CLASS_BY_TAG = {
    h1: 'heading-h1',
    h2: 'heading-h2',
    h3: 'heading-h3',
    h4: 'heading-h4',
    h5: 'heading-h5',
    h6: 'heading-h6',
};

const ALL_HEADING_CLASSES = Object.values(HEADING_CLASS_BY_TAG);

const getCommandState = (rte, command) => {
    try {
        return rte.doc?.queryCommandState(command) ? 1 : 0;
    } catch (error) {
        return 0;
    }
};

const getSelectionWrapSpan = (rte) => {
    try {
        const sel = rte.doc?.getSelection?.();
        if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return null;
        let node = sel.getRangeAt(0).commonAncestorContainer;
        if (node && node.nodeType === 3) node = node.parentNode;
        if (
            node &&
            node.tagName === 'SPAN' &&
            (sel.toString() || '').trim() === (node.textContent || '').trim()
        ) {
            return node;
        }
    } catch (_) { }
    return null;
};


const selectWrappedSpan = (editor, marker, attempts = 12) => {
    const component = editor.getWrapper?.()?.find?.(`.${marker}`)?.[0];
    if (component) {
        component.removeClass(marker);
        component.set('custom-name', 'Text-Stil');
        editor.select(component);
        editor.Panels?.getButton?.('views', 'open-sm')?.set('active', true);
        return;
    }
    if (attempts > 0) {
        requestAnimationFrame(() => selectWrappedSpan(editor, marker, attempts - 1));
    }
};


const styleSelection = (rte, editor) => {
    const sel = rte.doc?.getSelection?.();
    if (!editor || !sel || sel.rangeCount === 0 || sel.isCollapsed) return;
    const marker = `gjs-rte-marker-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    const existing = getSelectionWrapSpan(rte);

    if (existing) {
        existing.classList.add(marker);
    } else {
        const range = sel.getRangeAt(0);
        const span = rte.doc.createElement('span');
        span.className = marker;
        try {
            range.surroundContents(span);
        } catch (_) {
            span.appendChild(range.extractContents());
            range.insertNode(span);
        }
    }

    editor.RichTextEditor?.disable();
    selectWrappedSpan(editor, marker);
};

const HEADING_TYPE = 'heading';
const PARAGRAPH_TYPE = 'paragraph';
const BLOCK_COMPONENT_TYPES = [HEADING_TYPE, PARAGRAPH_TYPE];

const getSelectEl = (action) => action?.btn?.querySelector?.('select') || null;


const getEditedBlockComponent = (editorRef) => {
    const editor = editorRef?.current;
    if (!editor) return null;
    const component = editor.getSelected?.();
    const type = component?.get?.('type');
    return component && BLOCK_COMPONENT_TYPES.includes(type) ? component : null;
};

const syncLiveComponentContent = (component) => {
    const liveInnerHTML = component?.view?.el?.innerHTML;
    if (typeof liveInnerHTML !== 'string') return;
    component.components(liveInnerHTML);
};

const convertComponentType = (editor, component, type, tagName) => {
    const classes = component.getClasses().filter((cls) => !ALL_HEADING_CLASSES.includes(cls));
    const attributes = { ...component.getAttributes() };
    delete attributes['heading-size'];
    delete attributes.class;
    const liveInnerHTML = component.view?.el?.innerHTML;
    const content = typeof liveInnerHTML === 'string' ? liveInnerHTML : component.getInnerHTML();

    const replaced = component.replaceWith({
        type,
        tagName,
        classes,
        attributes,
        components: content,
    });

    const newComponent = Array.isArray(replaced) ? replaced[0] : replaced;
    if (newComponent) editor?.select?.(newComponent);
    return newComponent;
};

const applyBlockFormat = (editor, component, value) => {
    const currentType = component.get('type');

    if (value === 'p') {
        if (currentType === PARAGRAPH_TYPE) {
            if (component.get('tagName') !== 'p') component.set('tagName', 'p');
        } else {
            convertComponentType(editor, component, PARAGRAPH_TYPE, 'p');
        }
        return;
    }

    if (currentType === HEADING_TYPE) {
        syncLiveComponentContent(component);
        component.set('tagName', value);
    } else {
        convertComponentType(editor, component, HEADING_TYPE, value);
    }
};


const getSelectedBlockEl = (rte) => {
    try {
        const sel = rte.doc?.getSelection?.();
        if (!sel || sel.rangeCount === 0) return null;

        let node = sel.getRangeAt(0).commonAncestorContainer;
        if (node && node.nodeType === 3) node = node.parentNode;

        while (node && node.getAttribute?.('contenteditable') !== 'true') {
            const tag = node.tagName?.toLowerCase();
            if (tag && BLOCK_TAGS.includes(tag)) return node;
            node = node.parentNode;
        }
    } catch (_) { }
    return null;
};

const syncHeadingClass = (rte, value) => {
    const block = getSelectedBlockEl(rte);
    if (!block || !block.classList) return;

    ALL_HEADING_CLASSES.forEach((cls) => block.classList.remove(cls));

    const headingClass = HEADING_CLASS_BY_TAG[value];
    if (headingClass) block.classList.add(headingClass);
};

export const getRichTextToolbarActions = (editorRef) => [
    {
        name: 'formatBlock',
        icon: `<select class="gjs-field gjs-rte-format-select">
                <option value="p">Paragraph</option>
                <option value="h1">Heading 1</option>
                <option value="h2">Heading 2</option>
                <option value="h3">Heading 3</option>
                <option value="h4">Heading 4</option>
                <option value="h5">Heading 5</option>
                <option value="h6">Heading 6</option>
            </select>`,
        event: 'change',
        result: (rte, action) => {
            const select = getSelectEl(action);
            const value = BLOCK_TAGS.includes(select?.value) ? select.value : 'p';
            const block = getEditedBlockComponent(editorRef);
            if (block) {
                const editor = editorRef.current;
                editor?.RichTextEditor?.disable();
                applyBlockFormat(editor, block, value);
                return;
            }

            rte.exec('formatBlock', value.toUpperCase());
            syncHeadingClass(rte, value);
        },
        update: (rte, action) => {
            const select = getSelectEl(action);
            if (!select) return;


            const block = getEditedBlockComponent(editorRef);
            if (block) {
                const tag = (block.get('tagName') || '').toLowerCase();
                select.value = BLOCK_TAGS.includes(tag) ? tag : 'p';
                return;
            }

            const value = (rte.doc?.queryCommandValue('formatBlock') || '')
                .replace(/[<>"]/g, '')
                .toLowerCase();
            select.value = BLOCK_TAGS.includes(value) ? value : 'p';
        },
    },
    {
        name: 'bold',
        icon: '<b>B</b>',
        result: (rte) => rte.exec('bold'),
        state: (rte) => getCommandState(rte, 'bold'),
    },
    {
        name: 'italic',
        icon: '<i>I</i>',
        result: (rte) => rte.exec('italic'),
        state: (rte) => getCommandState(rte, 'italic'),
    },
    {
        name: 'underline',
        icon: '<u>U</u>',
        result: (rte) => rte.exec('underline'),
        state: (rte) => getCommandState(rte, 'underline'),
    },
    {
        name: 'strikeThrough',
        icon: '<s>S</s>',
        result: (rte) => rte.exec('strikeThrough'),
        state: (rte) => getCommandState(rte, 'strikeThrough'),
    },
    {
        name: 'link',
        icon: `<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <path fill="currentColor" d="M10.59,13.41A1.996,1.996 0 0,0 13.41,13.41L17.66,9.17A2,2 0 0,0 14.83,6.34L12.7,8.47L11.29,7.06L13.41,4.93A4,4 0 1,1 19.07,10.59L14.83,14.83A4,4 0 0,1 9.17,14.83L10.59,13.41M6.34,14.83A2,2 0 0,0 9.17,17.66L11.29,15.54L12.7,16.95L10.59,19.07A4,4 0 1,1 4.93,13.41L9.17,9.17A4,4 0 0,1 14.83,9.17L13.41,10.59A2,2 0 0,0 10.59,10.59L6.34,14.83Z" />
            </svg>`,
        state: (rte) => {
            try {
                const sel = rte.doc.getSelection();
                if (!sel || sel.rangeCount === 0) return 0;
                let node = sel.getRangeAt(0).commonAncestorContainer;
                if (node.nodeType === 3) node = node.parentNode;
                while (node && node.getAttribute?.('contenteditable') !== 'true') {
                    if (node.tagName === 'A') return 1;
                    node = node.parentNode;
                }
            } catch (_) { }
            return 0;
        },
        result: (rte) => {
            const sel = rte.doc.getSelection();
            let existingAnchor = null;
            if (sel && sel.rangeCount > 0) {
                let cur = sel.getRangeAt(0).commonAncestorContainer;
                if (cur.nodeType === 3) cur = cur.parentNode;
                while (cur && cur.getAttribute?.('contenteditable') !== 'true') {
                    if (cur.tagName === 'A') { existingAnchor = cur; break; }
                    cur = cur.parentNode;
                }
            }

            if (existingAnchor) {
                rte.exec('unlink');
                return;
            }

            const tempHref = `#`;
            rte.exec('createLink', tempHref);
            const editor = editorRef.current;
            if (!editor) return;
            editor.RichTextEditor.disable();
        },
    },
    {
        name: 'styleSelection',
        icon: `<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <path fill="currentColor" d="M18,4V3A1,1 0 0,0 17,2H5A1,1 0 0,0 4,3V7A1,1 0 0,0 5,8H17A1,1 0 0,0 18,7V6H19V10H9V21A1,1 0 0,0 10,22H12A1,1 0 0,0 13,21V12H21V4H18Z" />
            </svg>`,
        attributes: { title: 'Auswahl gestalten (CSS)' },
        result: (rte) => styleSelection(rte, editorRef.current),
    },
    {
        name: 'justifyLeft',
        icon: `<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <path fill="currentColor" d="M3 3H21V5H3V3M3 7H15V9H3V7M3 11H21V13H3V11M3 15H15V17H3V15M3 19H21V21H3V19Z" />
            </svg>`,
        result: (rte) => rte.exec('justifyLeft'),
        state: (rte) => getCommandState(rte, 'justifyLeft'),
    },
    {
        name: 'justifyCenter',
        icon: `<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <path fill="currentColor" d="M3 3H21V5H3V3M6 7H18V9H6V7M3 11H21V13H3V11M6 15H18V17H6V15M3 19H21V21H3V19Z" />
            </svg>`,
        result: (rte) => rte.exec('justifyCenter'),
        state: (rte) => getCommandState(rte, 'justifyCenter'),
    },
    {
        name: 'justifyRight',
        icon: `<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <path fill="currentColor" d="M3 3H21V5H3V3M9 7H21V9H9V7M3 11H21V13H3V11M9 15H21V17H9V15M3 19H21V21H3V19Z" />
            </svg>`,
        result: (rte) => rte.exec('justifyRight'),
        state: (rte) => getCommandState(rte, 'justifyRight'),
    },
    {
        name: 'justifyFull',
        icon: `<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <path fill="currentColor" d="M3 3H21V5H3V3M3 7H21V9H3V7M3 11H21V13H3V11M3 15H21V17H3V15M3 19H21V21H3V19Z" />
            </svg>`,
        result: (rte) => rte.exec('justifyFull'),
        state: (rte) => getCommandState(rte, 'justifyFull'),
    },
    {
        name: 'insertUnorderedList',
        icon: `<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <path fill="currentColor" d="M4 5.5A1.5 1.5 0 1 1 4 8.5A1.5 1.5 0 0 1 4 5.5M8 6H21V8H8V6M4 11.5A1.5 1.5 0 1 1 4 14.5A1.5 1.5 0 0 1 4 11.5M8 12H21V14H8V12M4 17.5A1.5 1.5 0 1 1 4 20.5A1.5 1.5 0 0 1 4 17.5M8 18H21V20H8V18Z" />
            </svg>`,
        result: (rte) => rte.exec('insertUnorderedList'),
        state: (rte) => getCommandState(rte, 'insertUnorderedList'),
    },
    {
        name: 'insertOrderedList',
        icon: `<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <path fill="currentColor" d="M3 6H5V8H2V7C2 5.9 2.9 5 4 5H5V4H2V2H5A2 2 0 0 1 7 4V8A2 2 0 0 1 5 10H3V8H5V6H3M8 6H21V8H8V6M4 12A2 2 0 0 1 6 14V16A2 2 0 0 1 4 18H2V16H4V14H2V12H4M8 12H21V14H8V12M2 20V19C2 17.9 2.9 17 4 17H7V19H4V20H7V22H2V20M8 18H21V20H8V18Z" />
            </svg>`,
        result: (rte) => rte.exec('insertOrderedList'),
        state: (rte) => getCommandState(rte, 'insertOrderedList'),
    },
];

export const applyGrapesRteToolbarStyles = () => {
    if (document.getElementById('gjs-rte-toolbar-style')) {
        return;
    }

    const style = document.createElement('style');
    style.id = 'gjs-rte-toolbar-style';
    style.innerHTML = `
        .gjs-rte-toolbar {
            background: #ffffff !important;
            border: 1px solid #e5e7eb !important;
            border-radius: 12px !important;
            box-shadow: 0 10px 25px rgba(15, 23, 42, 0.12) !important;
            padding: 8px !important;
            display: inline-flex !important;
            gap: 4px !important;
            align-items: center !important;
            flex-wrap: nowrap !important;
            overflow: visible !important;
        }

        .gjs-rte-toolbar .gjs-rte-action,
        .gjs-rte-toolbar [data-rte-action] {
            border: 1px solid transparent !important;
            border-radius: 8px !important;
            background: transparent !important;
            color: #1f2937 !important;
            fill: currentColor !important;
            min-width: 34px !important;
            min-height: 34px !important;
            padding: 6px !important;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            cursor: pointer !important;
            opacity: 1 !important;
            width: unset!important;
        }

        .gjs-rte-toolbar .gjs-rte-action:hover,
        .gjs-rte-toolbar [data-rte-action]:hover {
            background: #f3f4f6 !important;
            border-color: #e5e7eb !important;
        }

        .gjs-rte-toolbar .gjs-rte-action--active,
        .gjs-rte-toolbar [data-rte-action].active {
            background: #eaf2ff !important;
            border-color: #dbeafe !important;
            color: #2563eb !important;
        }

        .gjs-rte-toolbar .gjs-rte-format-select {
            border: 1px solid #d1d5db !important;
            border-radius: 10px !important;
            background: #ffffff !important;
            color: #111827 !important;
            font-size: 16px !important;
            height: 40px !important;
            line-height: 40px !important;
            padding: 0 34px 0 14px !important;
            min-width: 190px !important;
            appearance: none !important;
            outline: none !important;
            cursor: pointer !important;
        }

        .gjs-rte-toolbar .gjs-rte-action svg,
        .gjs-rte-toolbar [data-rte-action] svg {
            width: 18px !important;
            height: 18px !important;
            display: block !important;
        }

        .gjs-rte-toolbar .gjs-rte-action b,
        .gjs-rte-toolbar .gjs-rte-action i,
        .gjs-rte-toolbar .gjs-rte-action u,
        .gjs-rte-toolbar .gjs-rte-action s {
            font-size: 18px !important;
            line-height: 1 !important;
            color: currentColor !important;
        }
    `;

    document.head.appendChild(style);
};